import useSWR from 'swr'
import React from 'react'
import { components, type MenuListProps } from 'react-select'

import { getClients } from '../../api'
import { useAlerts } from '../../context/alerts'
import SelectBox from '../ui/selectBox'
import Spinner from '../ui/spinner'
import FieldLabel from '../ui/input/FieldLabel'
import InvoiceClientInfo from './invoice-info'
import { ReactComponent as PlusIcon } from '../../static/images/plus.svg'
import './selector.scss'

export const NEW_CLIENT_OPTION_VALUE = '__new_client__'

type Props = {
  onChange: (e: any) => void
  defaultValue?: string // client id selected by default
  required?: boolean
  disabled?: boolean
  showAll?: boolean
  value?: string
  size?: 'md' | 'sm' | 'lg'
  label?: string
  placeHolder?: string
  isClearable?: boolean
  showNewClient?: boolean
  onNewClient?: () => void
  showClientInfo?: boolean
}

const ClientSelector: React.FC<Props> = ({
  placeHolder,
  showAll,
  defaultValue,
  onChange,
  value,
  disabled,
  required,
  label = 'Client',
  isClearable,
  showNewClient,
  onNewClient,
  showClientInfo,
}) => {
  const { data, error, isLoading } = useSWR('/api/clients', getClients)
  const { addAlert } = useAlerts()

  if (isLoading) return <Spinner />

  if (error) {
    addAlert('error', error.message)
  }

  let _defaultValue = defaultValue
  if (data && data.length === 1 && !showAll) {
    // if there is only 1 client we select it by default
    _defaultValue = String(data[0]._id)
  }
  let options: { label: string, value: string }[] = []
  if (data) {
    options = options.concat(data?.map((client: any) => ({
      label: client.tradingName,
      value: String(client._id),
    })))
  }
  const hasControlledValue = value != null && String(value).trim() !== ''
  if (hasControlledValue) {
    const selectedId = String(value)
    if (!options.some((o) => o.value === selectedId)) {
      const existing = data?.find((c: any) => String(c._id) === selectedId)
      options = [
        ...options,
        {
          label: existing?.tradingName ?? 'Selected client',
          value: selectedId,
        },
      ]
    }
  }
  if (showNewClient) {
    options = [{ label: 'New client', value: NEW_CLIENT_OPTION_VALUE }, ...options]
  }
  const selectedClientId = hasControlledValue
    ? String(value)
    : (_defaultValue != null ? String(_defaultValue) : undefined)

  const handleChange = (e: { target: { name: string, value: string } }) => {
    if (e.target.value === NEW_CLIENT_OPTION_VALUE) {
      onNewClient?.()
      return
    }
    onChange(e)
  }

  const controlledValue = showNewClient
    ? (hasControlledValue ? String(value) : '')
    : (hasControlledValue ? String(value) : undefined)

  const formatOptionLabel = (option: { label: string, value: string }) => {
    if (option.value === NEW_CLIENT_OPTION_VALUE) {
      return (
        <span className='d-inline-flex align-items-center gap-1'>
          <PlusIcon width={14} height={14} />
          New client
        </span>
      )
    }
    return option.label
  }

  const MenuList = (props: MenuListProps<{ label: string, value: string }>) => {
    const childArray = React.Children.toArray(props.children)
    const hasClientOptions = childArray.length > 1

    return (
      <components.MenuList {...props}>
        {childArray[0]}
        {hasClientOptions && (
          <div className='client-selector__separator' aria-hidden='true'>
            <span>or select a client</span>
          </div>
        )}
        {childArray.slice(1)}
      </components.MenuList>
    )
  }

  return (
    <>
      {label && <FieldLabel label={label} htmlFor='client' required={required} />}
      <div className='bg-white'>
        <SelectBox
          key={controlledValue !== undefined ? `client-${controlledValue || 'empty'}` : 'client-empty'}
          id='client'
          required={required}
          name='clientId'
          value={controlledValue}
          defaultValue={controlledValue === undefined ? (_defaultValue as string | undefined) : undefined}
          options={options}
          onChange={handleChange}
          disabled={disabled}
          noBorder={Boolean(selectedClientId)}
          placeHolder={placeHolder ?? 'Select a client'}
          isClearable={isClearable}
          formatOptionLabel={showNewClient ? formatOptionLabel : undefined}
          components={showNewClient ? { MenuList } : undefined}
        />
        {selectedClientId && showClientInfo && (
          <InvoiceClientInfo clientId={selectedClientId} />
        )}
      </div>
    </>
  )
}

export default ClientSelector
